import { useEffect, useRef } from "react";
import { useAlert } from "../../context/AlertContext";
import AuthService from "../../api/AuthService";
import { CHECKOUT_PENDING_KEY } from "../checkout";
import { TOPUP_PENDING_PURCHASE_KEY } from "../topupCheckout";

// Handles re-entry after a Dodo hosted-checkout redirect.
//  - ?payment=success  -> reconcile subscription state, success toast, strip param.
//  - returned with a checkout pending but no success param -> ambiguous/failed
//    (no ?payment=failed contract exists), gentle info prompt.
// Idempotent: runs once, strips the param so a refresh doesn't re-toast.
//
// NOTE: the doc suggests refetching GET /subscription/summary, but the backend
// confirmed that endpoint is SuperAdmin-only. getProfileView() is the
// authoritative self-service reconcile (updates is_subscription / billing_info /
// isExpired / isTrialExpired and fires "subscription_updated").
export default function PaymentReturnHandler() {
  const { showAlert } = useAlert();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.search);
    const result = params.get("payment");
    const wasCheckoutPending = localStorage.getItem(CHECKOUT_PENDING_KEY) === "1";

    // A topup return is owned by the Topups screen, which reconciles it against
    // the backend. Bail out so this subscription-specific handler never claims
    // "your subscription is up to date" for a topup payment. Subscription
    // returns are untouched: neither marker is present for them.
    const isTopupReturn =
      params.has("purchaseId") ||
      !!localStorage.getItem(TOPUP_PENDING_PURCHASE_KEY);
    if (isTopupReturn) {
      localStorage.removeItem(CHECKOUT_PENDING_KEY);
      return;
    }

    const stripPaymentParam = () => {
      params.delete("payment");
      const search = params.toString();
      const url = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", url);
    };

    if (result === "success") {
      localStorage.removeItem(CHECKOUT_PENDING_KEY);
      // Reconcile authoritative state; the webhook has almost certainly already
      // finalized the payment by the time the browser lands here.
      if (localStorage.getItem("accessToken")) {
        AuthService.getProfileView().catch(() => {});
      }
      showAlert("Payment successful. Your subscription is up to date.", "success");
      stripPaymentParam();
      return;
    }

    // No success param but we had a checkout in flight -> outcome unknown.
    if (wasCheckoutPending) {
      localStorage.removeItem(CHECKOUT_PENDING_KEY);
      showAlert(
        "We couldn't confirm your payment. Please check your subscription status.",
        "info"
      );
    }
  }, [showAlert]);

  return null;
}
